// src/pages/buyer/CheckoutPage.jsx
import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { checkoutApi, cartApi, addressApi, ordersApi } from '../../api/services'
import { Button, Card, CardHeader, CardBody, PageSpinner } from '../../components/common/ui'
import { useAuth } from '../../context/AuthContext'
import GuestOtpLogin from '../../components/checkout/GuestOtpLogin'
import toast from 'react-hot-toast'

export default function CheckoutPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user, refreshUser } = useAuth()
  const [addressId, setAddressId] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState('razorpay')
  const [loyaltyPoints, setLoyaltyPoints] = useState(0)
  const [giftCode, setGiftCode] = useState('')
  const [notes, setNotes] = useState('')
  const [guestVerified, setGuestVerified] = useState(false)

  const isAuthenticated = !!user
  const canCheckout = isAuthenticated || guestVerified

  const { data: cartData } = useQuery({ queryKey: ['cart'], queryFn: () => cartApi.get(), enabled: canCheckout })
  const { data: addrData } = useQuery({ queryKey: ['addresses'], queryFn: () => addressApi.list(), enabled: canCheckout })
  const { data: ordersData } = useQuery({ queryKey: ['buyer-orders-count'], queryFn: () => ordersApi.list({ per_page: 1 }), enabled: canCheckout })
  const cart = cartData?.data?.data || cartData?.data
  const addresses = addrData?.data?.data || []
  const defaultAddr = addresses.find(a => a.is_default) || addresses[0]
  const selectedAddr = addresses.find(a => a.id === (addressId || defaultAddr?.id))
  const isFirstOrder = (ordersData?.data?.meta?.total ?? ordersData?.data?.total ?? null) === 0

  const placeOrder = useMutation({
    mutationFn: () => checkoutApi.placeOrder({
      address_id: selectedAddr?.id,
      payment_method: paymentMethod,
      loyalty_points: loyaltyPoints,
      gift_card_code: giftCode || undefined,
      notes: notes || undefined,
      apply_first_order_discount: isFirstOrder || undefined,
    }),
    onSuccess: (res) => {
      const order = res?.data?.data?.order || res?.data?.data
      if (paymentMethod === 'cod') {
        qc.invalidateQueries(['cart'])
        navigate(`/buyer/orders/success?order_id=${order?.id}`)
      } else {
        const rzp = res?.data?.data?.razorpay_order
        if (rzp && window.Razorpay) {
          const rzpInstance = new window.Razorpay({
            key: import.meta.env.VITE_RAZORPAY_KEY,
            amount: rzp.amount,
            currency: rzp.currency,
            order_id: rzp.id,
            name: 'Fabrinza',
            handler: async (response) => {
              await checkoutApi.verifyPayment({ ...response, order_id: order.id })
              qc.invalidateQueries(['cart'])
              navigate(`/buyer/orders/success?order_id=${order.id}`)
            },
          })
          rzpInstance.open()
        } else {
          navigate(`/buyer/orders/success?order_id=${order?.id}`)
        }
      }
    },
    onError: e => toast.error(e.response?.data?.message || 'Could not place order'),
  })

  // Guest checkout: show OTP login first
  if (!canCheckout) {
    return (
      <div className="max-w-md mx-auto py-8">
        <h1 className="text-xl font-display font-bold mb-2 text-center">Checkout</h1>
        <p className="text-sm text-muted text-center mb-6">Verify your mobile number to continue</p>
        <GuestOtpLogin onSuccess={() => { setGuestVerified(true); refreshUser?.() }} />
        <p className="text-xs text-center text-muted mt-4">
          Already have an account? <Link to="/login" className="text-pink font-bold hover:underline">Log in</Link>
        </p>
      </div>
    )
  }

  if (!cart) return <PageSpinner />

  const items = cart.items || []
  const subtotal = items.reduce((s, i) => s + (i.total || i.price * i.quantity || 0), 0)

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl font-display font-bold mb-6">Checkout</h1>

      {isFirstOrder && (
        <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
          <span className="text-2xl">🎉</span>
          <div>
            <p className="text-sm font-bold text-green-800">First Order Discount!</p>
            <p className="text-xs text-green-600">Welcome to Fabrinza — your first-order discount will be applied automatically.</p>
          </div>
        </div>
      )}

      {user?.is_guest && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3">
          <span className="text-xl">📱</span>
          <div>
            <p className="text-sm font-bold text-blue-800">Guest Checkout</p>
            <p className="text-xs text-blue-600">You're checking out as a guest. <Link to="/register" className="text-pink font-bold underline">Create an account</Link> to track orders and earn rewards.</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {/* Address */}
          <Card>
            <CardHeader><span className="font-bold text-sm">Delivery Address</span></CardHeader>
            <CardBody>
              {addresses.length === 0 ? (
                <div>
                  <p className="text-sm text-muted mb-2">No saved addresses.</p>
                  <Link to="/buyer/addresses"><Button size="sm" variant="secondary">Add Address</Button></Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {addresses.map(a => (
                    <button key={a.id} onClick={() => setAddressId(a.id)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${(addressId || defaultAddr?.id) === a.id ? 'border-pink bg-pink-light' : 'border-border hover:border-pink'}`}>
                      <p className="text-sm font-bold">{a.full_name} <span className="text-xs font-normal text-muted">({a.label})</span></p>
                      <p className="text-xs text-gray-600">{a.address_line1}, {a.city}, {a.state} – {a.pincode}</p>
                      <p className="text-xs text-muted">{a.mobile}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Payment */}
          <Card>
            <CardHeader><span className="font-bold text-sm">Payment Method</span></CardHeader>
            <CardBody className="space-y-2">
              {[['razorpay', 'Online Payment (Card / UPI / NetBanking)', '💳'], ['cod', 'Cash on Delivery', '💵'], ['bnpl', 'Fabrinza Pay Later (Pay in 3 EMIs)', '🏦']].map(([val, label, icon]) => (
                <button key={val} onClick={() => setPaymentMethod(val)}
                  className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${paymentMethod === val ? 'border-pink bg-pink-light' : 'border-border hover:border-pink'}`}>
                  <span className="text-xl">{icon}</span>
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </CardBody>
          </Card>
        </div>

        {/* Summary */}
        <div>
          <Card>
            <CardHeader><span className="font-bold text-sm">Order Summary</span></CardHeader>
            <CardBody>
              <div className="space-y-2 divide-y divide-border">
                <div className="space-y-2 pb-2">
                  {items.map(item => (
                    <div key={item.id} className="flex justify-between text-sm gap-2">
                      <span className="text-gray-700 line-clamp-1 flex-1">{item.product_name || item.product?.name} × {item.quantity}m</span>
                      <span className="font-medium whitespace-nowrap">₹{Number(item.total || item.price * item.quantity).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-muted">Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
                  {cart.discount_amount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-₹{Number(cart.discount_amount).toLocaleString('en-IN')}</span></div>}
                  <div className="flex justify-between font-bold text-base border-t border-border pt-2 mt-2">
                    <span>Total</span>
                    <span>₹{Number(cart.total || subtotal).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              <Button className="w-full mt-5" size="lg" loading={placeOrder.isPending}
                disabled={items.length === 0 || !selectedAddr}
                onClick={() => placeOrder.mutate()}>
                {paymentMethod === 'cod' ? 'Place Order' : paymentMethod === 'bnpl' ? 'Confirm — Pay Later' : 'Pay Now'}
              </Button>

              {!selectedAddr && <p className="text-xs text-red-500 mt-2 text-center">Please add a delivery address</p>}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}
